import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  People,
  EventNote,
  TrendingUp,
  AssignmentInd,
  Edit,
  Image as ImageIcon,
  Search,
  VerifiedUser,
} from '@mui/icons-material';
import apiService from '../services/api';

/** True when a proof-of-address issue date is more than ~3 months old. */
export const isStaleAddressProof = (isoDate) => {
  const issued = new Date(isoDate);
  if (Number.isNaN(issued.getTime())) return false;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 3);
  return issued < cutoff;
};

// ---- Overview tab ----------------------------------------------------------

const StatCard = ({ icon, color, value, label, sub }) => (
  <Card sx={{ p: 1, height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const OverviewTab = () => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await apiService.getAdminStats();
        if (active) setStats(data);
      } catch (err) {
        if (active) setError(err.message || 'Failed to load stats');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats) return null;

  const cards = [
    {
      icon: <People />,
      color: theme.palette.primary.main,
      value: stats.users.total,
      label: 'Total users',
      sub: `${stats.users.new_30d} new in 30d`,
    },
    {
      icon: <AssignmentInd />,
      color: theme.palette.secondary.main,
      value: stats.users.handlers,
      label: 'Handlers',
      sub: `${stats.users.active_handlers} available now`,
    },
    {
      icon: <People />,
      color: '#0EA5E9',
      value: stats.users.owners,
      label: 'Owners',
    },
    {
      icon: <EventNote />,
      color: '#F59E0B',
      value: stats.bookings.total,
      label: 'Bookings',
      sub: `${stats.bookings.last_30d} in 30d`,
    },
    {
      icon: <EventNote />,
      color: '#6366F1',
      value: stats.bookings.pending,
      label: 'Pending bookings',
      sub: `${stats.bookings.completed} completed`,
    },
    {
      icon: <TrendingUp />,
      color: theme.palette.success.main,
      value: `R${Number(stats.revenue.last_30d).toFixed(2)}`,
      label: 'Revenue (30d)',
      sub: 'Completed bookings',
    },
    {
      icon: <AssignmentInd />,
      color: '#EF4444',
      value: stats.applications.pending,
      label: 'Handler applications',
      sub: 'Awaiting review',
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((c, i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
          <StatCard {...c} />
        </Grid>
      ))}
    </Grid>
  );
};

// ---- Price Book tab --------------------------------------------------------

const emptyEdit = {
  name: '',
  icon: '',
  default_base_price: '',
  default_price_per_additional_pet: '',
  default_duration_minutes: '',
  max_pets: '',
  is_active: true,
};

const PriceBookTab = ({ onToast }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // the service type being edited
  const [form, setForm] = useState(emptyEdit);
  const [iconFile, setIconFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminServiceTypes();
      setTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load service types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (st) => {
    setEditing(st);
    setForm({
      name: st.name ?? '',
      icon: st.icon ?? '',
      default_base_price: st.default_base_price ?? '',
      default_price_per_additional_pet: st.default_price_per_additional_pet ?? '',
      default_duration_minutes: st.default_duration_minutes ?? '',
      max_pets: st.max_pets ?? '',
      is_active: !!st.is_active,
    });
    setIconFile(null);
    setBgFile(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setIconFile(null);
    setBgFile(null);
  };

  const handleField = (field) => (e) => {
    const value = field === 'is_active' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      // 1) Scalar fields as JSON (keeps booleans/decimals correctly typed).
      let updated = await apiService.updateAdminServiceType(editing.id, {
        name: form.name,
        icon: form.icon,
        default_base_price: form.default_base_price,
        default_price_per_additional_pet: form.default_price_per_additional_pet,
        default_duration_minutes: form.default_duration_minutes,
        max_pets: form.max_pets,
        is_active: form.is_active,
      });

      // 2) Any new artwork as a single multipart PATCH.
      const images = {};
      if (iconFile) images.icon_image = iconFile;
      if (bgFile) images.background_image = bgFile;
      if (Object.keys(images).length) {
        updated = await apiService.updateAdminServiceType(editing.id, images);
      }

      setTypes((prev) =>
        prev.map((t) => (t.id === editing.id ? { ...t, ...updated } : t)),
      );
      onToast('Service updated');
      closeEditor();
    } catch (err) {
      onToast(err.message || 'Failed to update service', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Base</TableCell>
              <TableCell align="right">+Pet</TableCell>
              <TableCell align="right">Mins</TableCell>
              <TableCell align="right">Max pets</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Bookings</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((st) => (
              <TableRow key={st.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      variant="rounded"
                      src={st.icon_image || undefined}
                      sx={{ width: 28, height: 28, bgcolor: 'grey.100', color: 'text.secondary' }}
                    >
                      {st.icon || <ImageIcon fontSize="small" />}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {st.name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{st.category_name}</TableCell>
                <TableCell align="right">R{st.default_base_price}</TableCell>
                <TableCell align="right">R{st.default_price_per_additional_pet}</TableCell>
                <TableCell align="right">{st.default_duration_minutes}</TableCell>
                <TableCell align="right">{st.max_pets}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={st.is_active ? 'Active' : 'Off'}
                    color={st.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">{st.booking_count}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditor(st)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={closeEditor} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {editing?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleField('name')}
              fullWidth
            />
            <TextField
              label="Icon (emoji or text, optional)"
              value={form.icon}
              onChange={handleField('icon')}
              helperText="Shown when no icon image is uploaded"
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Base price"
                type="number"
                value={form.default_base_price}
                onChange={handleField('default_base_price')}
                InputProps={{ startAdornment: <InputAdornment position="start">R</InputAdornment> }}
                fullWidth
              />
              <TextField
                label="Per additional pet"
                type="number"
                value={form.default_price_per_additional_pet}
                onChange={handleField('default_price_per_additional_pet')}
                InputProps={{ startAdornment: <InputAdornment position="start">R</InputAdornment> }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Duration (minutes)"
                type="number"
                value={form.default_duration_minutes}
                onChange={handleField('default_duration_minutes')}
                fullWidth
              />
              <TextField
                label="Max pets"
                type="number"
                value={form.max_pets}
                onChange={handleField('max_pets')}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={handleField('is_active')} />}
              label="Active (bookable)"
            />

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Artwork
              </Typography>
            </Divider>

            <ImagePicker
              label="Icon image"
              currentUrl={editing?.icon_image}
              file={iconFile}
              onPick={setIconFile}
            />
            <ImagePicker
              label="Background image"
              currentUrl={editing?.background_image}
              file={bgFile}
              onPick={setBgFile}
              wide
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ImagePicker = ({ label, currentUrl, file, onPick, wide }) => {
  const preview = file ? URL.createObjectURL(file) : currentUrl;
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: wide ? 96 : 56,
          height: 56,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          backgroundImage: preview ? `url(${preview})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          flexShrink: 0,
        }}
      >
        {!preview && <ImageIcon fontSize="small" />}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>
        <Button component="label" size="small" variant="outlined">
          {preview ? 'Change' : 'Upload'}
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
          />
        </Button>
        {file && (
          <Button size="small" color="inherit" onClick={() => onPick(null)} sx={{ ml: 1 }}>
            Undo
          </Button>
        )}
      </Box>
    </Stack>
  );
};

// ---- Users tab -------------------------------------------------------------

const ROLE_FILTERS = [
  { value: '', label: 'All roles' },
  { value: 'owner', label: 'Owners' },
  { value: 'handler', label: 'Handlers' },
  { value: 'staff', label: 'Staff' },
];

const USER_FLAGS = [
  { key: 'is_active', label: 'Active' },
  { key: 'is_owner', label: 'Owner' },
  { key: 'is_handler', label: 'Handler' },
  { key: 'is_staff', label: 'Staff' },
];

const UsersTab = ({ onToast }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminUsers({ search, role });
      setUsers(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, role]);

  // Debounce the search box; role changes apply immediately.
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const toggleFlag = async (user, key) => {
    const next = !user[key];
    // Optimistic; revert on failure.
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, [key]: next } : u)));
    try {
      await apiService.updateAdminUser(user.id, { [key]: next });
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, [key]: !next } : u)));
      onToast(err.message || 'Update failed', 'error');
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {ROLE_FILTERS.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="center">Bookings</TableCell>
                {USER_FLAGS.map((f) => (
                  <TableCell key={f.key} align="center">
                    {f.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {(u.first_name || u.email || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {u.full_name || `${u.first_name} ${u.last_name}`.trim() || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {u.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{u.booking_count}</TableCell>
                  {USER_FLAGS.map((f) => (
                    <TableCell key={f.key} align="center">
                      <Switch
                        size="small"
                        checked={!!u[f.key]}
                        onChange={() => toggleFlag(u, f.key)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2 + USER_FLAGS.length} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No users match your filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

// ---- Handler applications tab ---------------------------------------------

const STATUS_COLOR = { pending: 'warning', approved: 'success', rejected: 'default' };

const ApplicationsTab = ({ onToast }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getHandlerApplications();
      setApps(Array.isArray(data) ? data : data?.results || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (app, action) => {
    let reason = '';
    if (action === 'reject') {
      // eslint-disable-next-line no-alert
      reason = window.prompt('Reason for rejection (optional):', '') ?? '';
    }
    setBusyId(app.id);
    try {
      const updated =
        action === 'approve'
          ? await apiService.approveHandlerApplication(app.id)
          : await apiService.rejectHandlerApplication(app.id, reason);
      setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, ...updated } : a)));
      onToast(`Application ${action === 'approve' ? 'approved' : 'rejected'}`);
    } catch (err) {
      onToast(err.message || 'Action failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  const pending = apps.filter((a) => a.status === 'pending');
  const decided = apps.filter((a) => a.status !== 'pending');
  const applicant = (a) =>
    a.user?.full_name ||
    `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim() ||
    a.user?.email ||
    'Applicant';

  const AppCard = ({ a }) => (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {applicant(a)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {a.user?.email}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={a.status_display || a.status}
            color={STATUS_COLOR[a.status] || 'default'}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {a.motivation && (
          <Field label="Motivation" value={a.motivation} />
        )}
        {a.experience_description && (
          <Field label="Experience" value={a.experience_description} />
        )}
        {a.references && <Field label="References" value={a.references} />}

        {/* FICA pack: the ID number in mono, and the proof-of-address issue
            date flagged when it has gone stale since submission. */}
        {(a.sa_id_number || a.proof_of_address_date) && (
          <Stack direction="row" spacing={3} sx={{ mt: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
            {a.sa_id_number && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  SA ID number
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {a.sa_id_number}
                </Typography>
              </Box>
            )}
            {a.proof_of_address_date && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Proof of address issued
                </Typography>
                <Typography variant="body2">
                  {a.proof_of_address_date}
                  {isStaleAddressProof(a.proof_of_address_date) && (
                    <Chip
                      size="small"
                      color="warning"
                      label="older than 3 months"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
          {a.id_document && (
            <Button size="small" href={a.id_document} target="_blank" rel="noreferrer">
              Certified ID
            </Button>
          )}
          {a.proof_of_address && (
            <Button size="small" href={a.proof_of_address} target="_blank" rel="noreferrer">
              Proof of address
            </Button>
          )}
          {a.bank_confirmation && (
            <Button size="small" href={a.bank_confirmation} target="_blank" rel="noreferrer">
              Bank letter
            </Button>
          )}
          {a.background_check && (
            <Button size="small" href={a.background_check} target="_blank" rel="noreferrer">
              Background check
            </Button>
          )}
        </Stack>

        {a.status === 'pending' && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={busyId === a.id}
              onClick={() => decide(a, 'approve')}
            >
              {busyId === a.id ? <CircularProgress size={18} /> : 'Approve'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busyId === a.id}
              onClick={() => decide(a, 'reject')}
            >
              Deny
            </Button>
            {/* Phase 2: hand the FICA pack to a verification partner
                (see backend docs/fica-providers.md). Wired up but disabled
                until a provider is contracted — Tooltip needs the <span>
                because disabled buttons swallow pointer events. */}
            <Tooltip title="Automated FICA checks via a verification partner — coming soon. For now, review the documents and approve or deny manually.">
              <span>
                <Button variant="outlined" size="small" disabled startIcon={<VerifiedUser />}>
                  Send for verification
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
        {a.status !== 'pending' && a.admin_notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {a.admin_notes}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Pending ({pending.length})
        </Typography>
        {pending.length === 0 ? (
          <Alert severity="success" variant="outlined">
            No applications awaiting review.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {pending.map((a) => (
              <AppCard key={a.id} a={a} />
            ))}
          </Stack>
        )}
      </Box>

      {decided.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Reviewed ({decided.length})
          </Typography>
          <Stack spacing={2}>
            {decided.map((a) => (
              <AppCard key={a.id} a={a} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {value}
    </Typography>
  </Box>
);

// ---- Shell -----------------------------------------------------------------

const AdminDashboard = () => {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const showToast = (msg, severity = 'success') =>
    setToast({ open: true, msg, severity });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
          Admin Portal
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Platform overview, pricing, and user management.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Overview" />
          <Tab label="Price Book" />
          <Tab label="Users" />
          <Tab label="Applications" />
        </Tabs>
      </Card>

      {tab === 0 && <OverviewTab />}
      {tab === 1 && <PriceBookTab onToast={showToast} />}
      {tab === 2 && <UsersTab onToast={showToast} />}
      {tab === 3 && <ApplicationsTab onToast={showToast} />}

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
